import { CONFIG } from "./config";

/**
 * Level definitions + wall geometry (Worlds Update, spec-extension §W1).
 *
 * Walls are static solids the PLAYER collides with. Echoes replay recorded
 * routes, so they respect walls by construction and need no collision here.
 * All math below uses only +,-,*,/,sqrt,min,max,abs on doubles so the Swift
 * mirror (LevelDef.swift) is bit-identical — verified by shared vectors.
 */

export type Wall =
  | { type: "pillar"; x: number; y: number; r: number }
  | { type: "slab"; x: number; y: number; w: number; h: number; cr: number }
  /**
   * Gate (Worlds §W4): a slab oscillating along one axis on a TRIANGLE wave —
   * pure rational arithmetic, never sin/cos, so both engines are bit-identical.
   * Offset at step s: p = ((s + phaseSteps) mod periodSteps) / periodSteps;
   * tri = p < 0.5 ? 2p : 2 - 2p; offset = (2·tri − 1) · amplitude.
   */
  | {
      type: "gate";
      x: number;
      y: number;
      w: number;
      h: number;
      cr: number;
      axis: "x" | "y";
      amplitude: number;
      periodSteps: number;
      phaseSteps: number;
    };

/** Gate slab center at a given mechanics step (static shapes pass through). */
export function wallCenterAt(w: Wall, stepIndex: number): { x: number; y: number } {
  if (w.type !== "gate") return { x: w.x, y: w.y };
  const p = (((stepIndex + w.phaseSteps) % w.periodSteps) + w.periodSteps) % w.periodSteps;
  const frac = p / w.periodSteps;
  const tri = frac < 0.5 ? frac * 2 : 2 - frac * 2;
  const offset = (tri * 2 - 1) * w.amplitude;
  return w.axis === "x" ? { x: w.x + offset, y: w.y } : { x: w.x, y: w.y + offset };
}

export interface LevelEcho {
  id: "A" | "B" | "C" | "D" | "E" | "F";
  unlockStep: number;
}

export interface LevelDef {
  id: string;
  planet: string;
  index: number;
  seed: string;
  quota: number;
  star2: number;
  star3: number;
  echoes: LevelEcho[];
  walls: Wall[];
  /** Authored entity schedule (Worlds §W5); absent/empty before Korvath. */
  entities?: EntitySpawn[];
}

export interface PlanetDef {
  id: string;
  name: string;
  index: number;
  accent: string;
  unlockStars: number;
  comingSoon: boolean;
  levels: LevelDef[];
}

export interface LevelCatalog {
  schemaVersion: number;
  configVersion: number;
  planets: PlanetDef[];
}

/**
 * Signed distance from point to wall surface at a mechanics step (negative =
 * inside). Static shapes ignore the step; gates evaluate at their current
 * triangle-wave center.
 */
export function wallSdf(w: Wall, x: number, y: number, stepIndex: number): number {
  const c = wallCenterAt(w, stepIndex);
  if (w.type === "pillar") {
    const dx = x - c.x;
    const dy = y - c.y;
    return Math.sqrt(dx * dx + dy * dy) - w.r;
  }
  const hx = w.w / 2 - w.cr;
  const hy = w.h / 2 - w.cr;
  const dx = Math.abs(x - c.x) - hx;
  const dy = Math.abs(y - c.y) - hy;
  const qx = Math.max(dx, 0);
  const qy = Math.max(dy, 0);
  const outside = Math.sqrt(qx * qx + qy * qy);
  const inside = Math.min(Math.max(dx, dy), 0);
  return outside + inside - w.cr;
}

/**
 * Push a disc of radius `radius` out of every wall. Fixed iteration count and
 * fixed wall order — never a convergence loop — so both engines take the
 * exact same arithmetic path (spec §19 determinism rules).
 */
export function resolveWalls(
  px: number,
  py: number,
  walls: readonly Wall[],
  radius: number,
  stepIndex: number,
): { x: number; y: number } {
  let x = px;
  let y = py;
  for (let it = 0; it < CONFIG.wallPushIterations; it++) {
    for (const w of walls) {
      const sdf = wallSdf(w, x, y, stepIndex);
      const pen = radius - sdf;
      if (pen <= 0) continue;
      const c = wallCenterAt(w, stepIndex);
      let nx = 0;
      let ny = 0;
      if (w.type === "pillar") {
        const dx = x - c.x;
        const dy = y - c.y;
        const d = Math.sqrt(dx * dx + dy * dy);
        if (d > 1e-9) {
          nx = dx / d;
          ny = dy / d;
        } else {
          nx = 0;
          ny = -1;
        }
      } else {
        const hx = w.w / 2 - w.cr;
        const hy = w.h / 2 - w.cr;
        const rx = x - c.x;
        const ry = y - c.y;
        const dx = Math.abs(rx) - hx;
        const dy = Math.abs(ry) - hy;
        const qx = Math.max(dx, 0);
        const qy = Math.max(dy, 0);
        const qlen = Math.sqrt(qx * qx + qy * qy);
        if (qlen > 1e-9) {
          nx = (rx >= 0 ? qx : -qx) / qlen;
          ny = (ry >= 0 ? qy : -qy) / qlen;
        } else if (dx > dy) {
          nx = rx >= 0 ? 1 : -1;
          ny = 0;
        } else {
          nx = 0;
          ny = ry >= 0 ? 1 : -1;
        }
      }
      x += nx * pen;
      y += ny * pen;
    }
  }
  return { x, y };
}

/** Minimum SDF across all walls at a step (Infinity when there are none). */
export function nearestWallSdf(walls: readonly Wall[], x: number, y: number, stepIndex: number): number {
  let best = Infinity;
  for (const w of walls) {
    const d = wallSdf(w, x, y, stepIndex);
    if (d < best) best = d;
  }
  return best;
}

/**
 * SDF to a gate's FULL TRAVEL BAND — the slab stretched by its amplitude along
 * its axis. Shard placement uses this so a shard is never parked anywhere the
 * gate can reach (Worlds §W4). Static walls fall back to their plain SDF.
 */
export function wallBandSdf(w: Wall, x: number, y: number): number {
  if (w.type !== "gate") return wallSdf(w, x, y, 0);
  const ax = w.axis === "x" ? w.amplitude : 0;
  const ay = w.axis === "y" ? w.amplitude : 0;
  const hx = w.w / 2 - w.cr + ax;
  const hy = w.h / 2 - w.cr + ay;
  const dx = Math.abs(x - w.x) - hx;
  const dy = Math.abs(y - w.y) - hy;
  const qx = Math.max(dx, 0);
  const qy = Math.max(dy, 0);
  const outside = Math.sqrt(qx * qx + qy * qy);
  const inside = Math.min(Math.max(dx, dy), 0);
  return outside + inside - w.cr;
}

// ---------------------------------------------------------------- entities

/**
 * Level entities (Worlds §W5): authored spawns, deterministic by construction.
 * MINE     — telegraphs for mineTelegraphSteps, then armed; contact costs a
 *            life (shield absorbs it) and consumes the mine.
 * TIMELEAK — shard look-alike; collecting it steals timeLeakSteps off the clock.
 * SHIELD   — pickup; absorbs exactly one hit within shieldSteps.
 * STASIS   — pickup; freezes echo playback for stasisSteps (they stay solid).
 */
export type EntityKind =
  | "mine"      // armed hazard: contact costs a life
  | "timeleak"  // steals clock time
  | "glitch"    // inverts the drag axis (Worlds §W8)
  | "surge"     // echoes surge 1 s toward the present (Worlds §W8)
  | "shield"    // absorbs one hit
  | "stasis"    // freezes echo playback
  | "shockwave" // echoes blasted 1.5 s back along their timelines (Worlds §W8)
  | "overclock"; // boosted spark speed (Worlds §W8)

export interface EntitySpawn {
  kind: EntityKind;
  atStep: number;
  x: number;
  y: number;
}
